import { ApiError, type ApiOpts } from '@/lib/api';
import { createDemoAssessmentAdapter } from './demoAssessmentAdapter';
import {
  demoLearningAnalysis,
  demoLearningReviews,
  demoPracticeSet,
  DEMO_ANALYSIS_ID,
  DEMO_SET_ID,
  DEMO_WORKBOOK_ID,
} from './demoLearningOutcomes';

/** Prepared Year 10 outcomes; shares no learning or wallet state with a real account. */
export function createDemoMathPracticeAdapter() {
  const analysis = demoLearningAnalysis();
  const practiceSet = demoPracticeSet();
  const reviews = demoLearningReviews();
  const assessment = createDemoAssessmentAdapter(reviews);
  const attempts = new Map<string, { fingerprint: string; result: unknown }>();
  const completedReviews = new Map<string, { id: string; reviewed_at: string }>();
  const now = () => new Date().toISOString();
  const denied = () =>
    new ApiError(403, 'STUDY_EXPERIENCE_ONLY', 'Only prepared example answers are available.');

  function handle(path: string, opts: ApiOpts): { value: unknown } | undefined {
    const url = new URL(path, 'https://experience.invalid');
    const p = url.pathname;
    const method = opts.method ?? 'GET';
    const body = (opts.body ?? {}) as Record<string, unknown>;
    const assessmentResult = assessment(path, opts);
    if (assessmentResult !== undefined) return { value: assessmentResult };
    if (p === `/study/analyses/${DEMO_ANALYSIS_ID}` && method === 'GET') return { value: analysis };
    if (p === `/study/analyses/${DEMO_ANALYSIS_ID}/seen` && method === 'POST')
      return {
        value: { analysis_id: DEMO_ANALYSIS_ID, status: 'succeeded', learner_seen_at: now() },
      };
    if (p === `/study/analyses/${DEMO_ANALYSIS_ID}/practice-sets` && method === 'GET') {
      const requested = url.searchParams.get('set_id');
      if (requested && requested !== DEMO_SET_ID)
        throw new ApiError(404, 'EXAMPLE_NOT_FOUND', 'This practice set is not in this example.');
      return { value: { document_id: DEMO_WORKBOOK_ID, items: [practiceSet] } };
    }
    const attempt =
      /^\/study\/analyses\/demo-maths-reading\/practice-sets\/demo-maths-practice\/questions\/(pq[1-5])\/attempts$/.exec(
        p,
      );
    if (attempt && method === 'POST') {
      if (
        body.expected_stars !== 0 ||
        !['A', 'B', 'C', 'D'].includes(String(body.choice)) ||
        typeof body.idempotency_key !== 'string' ||
        !body.idempotency_key
      )
        throw denied();
      const fingerprint = JSON.stringify([attempt[1], body.choice]);
      const cached = attempts.get(body.idempotency_key);
      if (cached) {
        if (cached.fingerprint !== fingerprint) throw denied();
        return { value: cached.result };
      }
      const question = practiceSet.questions.find((row) => row.id === attempt[1]);
      if (!question) throw denied();
      const correct = body.choice === question.answer?.correct_option;
      question.attempts++;
      question.latest_attempt = {
        id: crypto.randomUUID(),
        question_id: question.id,
        practice_set_id: DEMO_SET_ID,
        status: 'checked',
        verdict: correct ? 'correct' : 'incorrect',
        hint: correct
          ? 'The selected option matches this prepared answer key.'
          : 'Check the original conditions and your working. Read the prepared explanation after trying.',
        student_answer_text: String(body.choice),
        stars: 0,
        stars_refunded: 0,
        refunded: false,
        error_code: null,
        created_at: now(),
        completed_at: now(),
      };
      const index = reviews.findIndex(
        (row) => row.question_id === question.id && row.practice_set_id === DEMO_SET_ID,
      );
      if (correct && index >= 0) reviews.splice(index, 1);
      if (!correct && index < 0)
        reviews.push({
          ...demoLearningReviews()[0],
          id: `demo-review-${question.id}`,
          question_id: question.id,
          question_number: question.id.slice(2),
          topic: question.stem,
        });
      const result = { practice_set_id: DEMO_SET_ID, attempt: question.latest_attempt, question };
      attempts.set(body.idempotency_key, { fingerprint, result: structuredClone(result) });
      return { value: result };
    }
    const done = /^\/study\/review\/analysis-items\/([^/]+)\/done$/.exec(p);
    if (done && method === 'POST') {
      const receipt = completedReviews.get(done[1]);
      if (receipt) return { value: receipt };
      const index = reviews.findIndex((row) => row.id === done[1]);
      if (index < 0) return undefined;
      reviews.splice(index, 1);
      const result = { id: done[1], reviewed_at: now() };
      completedReviews.set(done[1], result);
      return { value: result };
    }
    if (p === `/study/analyses/${DEMO_ANALYSIS_ID}/review` && method === 'POST') {
      let item = reviews.find((row) => row.source === 'learner_request');
      const created = !item;
      if (!item) {
        item = {
          ...demoLearningReviews()[0],
          id: 'demo-material-review',
          source: 'learner_request',
          question_id: null,
          question_number: null,
          practice_set_id: null,
          topic: 'Exact values and checking results',
          due: false,
          review_due_at: new Date(Date.now() + 3 * 86400000).toISOString(),
        };
        reviews.push(item);
      }
      return { value: { item, created } };
    }
    return undefined;
  }
  return { analysis, reviews, handle };
}
